import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Database, Eye, FileText, Loader2, Plus, Save, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { db } from "@/lib/billing";
import { defaultInvoiceTemplates, renderTemplateString, sampleVariables, type DefaultInvoiceTemplate } from "@/lib/templateDefaults";
import { safeTemplateHTML } from "@/lib/safeHtml";
import { useCompany } from "@/hooks/useCompany";

type InvoiceTemplate = DefaultInvoiceTemplate & { company_id?: string | null };

async function fetchTemplates(companyId: string | null, isPlatformOwner: boolean): Promise<InvoiceTemplate[]> {
  let query = db.from("invoice_templates").select("*").order("language").order("name");
  if (!isPlatformOwner && companyId) query = query.eq("company_id", companyId);
  const { data, error } = await query;
  if (error) {
    console.warn("invoice_templates unavailable; using local defaults", error);
    return defaultInvoiceTemplates;
  }
  if (data?.length) return data;
  const { data: seeded, error: seedError } = await db
    .from("invoice_templates")
    .upsert(defaultInvoiceTemplates.map((template) => ({
      template_key: template.template_key,
      name: template.name,
      language: template.language,
      company_name: template.company_name,
      company_logo_url: template.company_logo_url || null,
      company_legal_details: template.company_legal_details || null,
      footer_text: template.footer_text || null,
      payment_terms: template.payment_terms || null,
      html_body: template.html_body,
      variables: template.variables,
      is_default: template.is_default,
      is_enabled: template.is_enabled,
      company_id: companyId,
    })), { onConflict: "company_id,template_key" })
    .select("*")
    .order("language")
    .order("name");
  if (seedError) {
    console.warn("Could not seed default invoice templates; using local defaults", seedError);
    return defaultInvoiceTemplates;
  }
  return seeded?.length ? seeded : defaultInvoiceTemplates;
}

export default function AdminInvoiceTemplatesPage() {
  const queryClient = useQueryClient();
  const { currentCompanyId, isPlatformOwner } = useCompany();
  const [search, setSearch] = useState("");
  const [language, setLanguage] = useState("all");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState<InvoiceTemplate | null>(null);
  const [preview, setPreview] = useState<"html" | "pdf" | null>(null);
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["invoice-templates", currentCompanyId, isPlatformOwner],
    queryFn: () => fetchTemplates(currentCompanyId, isPlatformOwner),
    enabled: isPlatformOwner || !!currentCompanyId,
  });
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return templates.filter((template) => [template.name, template.template_key, template.language, template.company_name].join(" ").toLowerCase().includes(q)
      && (language === "all" || template.language === language)
      && (category === "all" || (category === "default" ? template.is_default : !template.is_default))
      && (status === "all" || (status === "active" ? template.is_enabled : !template.is_enabled)));
  }, [templates, search, language, category, status]);
  const active = selected ?? filtered[0] ?? null;

  const save = useMutation({
    mutationFn: async (template: InvoiceTemplate) => {
      const payload = {
        template_key: template.template_key,
        name: template.name,
        language: template.language,
        company_name: template.company_name,
        company_logo_url: template.company_logo_url || null,
        company_legal_details: template.company_legal_details || null,
        footer_text: template.footer_text || null,
        payment_terms: template.payment_terms || null,
        html_body: template.html_body,
        variables: template.variables,
        is_default: template.is_default,
        is_enabled: template.is_enabled,
        company_id: template.company_id ?? currentCompanyId,
      };
      const result = template.id
        ? await db.from("invoice_templates").update(payload).eq("id", template.id)
        : await db.from("invoice_templates").insert(payload);
      if (result.error) throw result.error;
    },
    onSuccess: () => {
      toast.success("Invoice template saved");
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ["invoice-templates"] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not save invoice template"),
  });

  const seedDefaults = useMutation({
    mutationFn: async () => {
      const { error } = await db.from("invoice_templates").upsert(defaultInvoiceTemplates.map((template) => ({
        template_key: template.template_key,
        name: template.name,
        language: template.language,
        company_name: template.company_name,
        company_logo_url: template.company_logo_url || null,
        company_legal_details: template.company_legal_details || null,
        footer_text: template.footer_text || null,
        payment_terms: template.payment_terms || null,
        html_body: template.html_body,
        variables: template.variables,
        is_default: template.is_default,
        is_enabled: template.is_enabled,
        company_id: currentCompanyId,
      })), { onConflict: "company_id,template_key" });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Default invoice templates seeded");
      queryClient.invalidateQueries({ queryKey: ["invoice-templates"] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not seed invoice templates"),
  });

  const remove = useMutation({
    mutationFn: async (template: InvoiceTemplate) => {
      if (!template.id) throw new Error("Seed templates must be saved before deletion.");
      const { error } = await db.from("invoice_templates").delete().eq("id", template.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Invoice template deleted");
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ["invoice-templates"] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not delete invoice template"),
  });

  const patch = (changes: Partial<InvoiceTemplate>) => setSelected({ ...(active as InvoiceTemplate), ...changes });
  const duplicate = (template: InvoiceTemplate) => setSelected({ ...template, id: undefined, template_key: `${template.template_key}_copy_${Date.now()}`, name: `${template.name} copy`, is_default: false });
  const createNew = () => setSelected({ ...defaultInvoiceTemplates[0], id: undefined, template_key: `custom_invoice_${Date.now()}`, name: "Custom invoice template", is_default: false, company_id: currentCompanyId });
  const renderedHtml = active ? renderTemplateString(active.html_body, { ...sampleVariables, line_items: "<tr><td>News Site Pro</td><td>49.00</td></tr>", subtotal: "49.00", tax: "0.00", total: "49.00", company_logo_url: "" } as any) : "";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Invoice Templates</h1>
          <p className="text-xs text-muted-foreground">Managed invoice HTML and PDF-ready layouts with company legal fields.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => seedDefaults.mutate()} disabled={seedDefaults.isPending}>
            {seedDefaults.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />}
            Seed defaults
          </Button>
          <Button variant="outline" size="sm" onClick={createNew}><Plus className="mr-2 h-4 w-4" />New</Button>
          {active && <Button size="sm" onClick={() => save.mutate(active)} disabled={save.isPending}>{save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save</Button>}
        </div>
      </header>
      <main className="grid gap-6 p-6 lg:grid-cols-[380px_1fr]">
        <Card>
          <CardHeader className="gap-3">
            <CardTitle className="text-base">Templates</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search templates..." value={search} onChange={(event) => setSearch(event.target.value)} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Select value={language} onValueChange={setLanguage}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All languages</SelectItem><SelectItem value="fr">FR</SelectItem><SelectItem value="en">EN</SelectItem></SelectContent></Select>
              <Select value={category} onValueChange={setCategory}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All types</SelectItem><SelectItem value="default">Default</SelectItem><SelectItem value="custom">Custom</SelectItem></SelectContent></Select>
              <Select value={status} onValueChange={setStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent></Select>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : filtered.length ? filtered.map((template) => (
              <button key={template.id ?? template.template_key} className="w-full rounded-md border border-border p-3 text-left hover:bg-accent" onClick={() => { setSelected(template); setPreview(null); }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{template.name}</span>
                  <Badge variant={template.is_enabled ? "default" : "secondary"}>{template.language.toUpperCase()}</Badge>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{template.template_key}{template.is_default ? " - default" : ""}</div>
              </button>
            )) : (
              <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                No invoice templates match these filters.
              </div>
            )}
          </CardContent>
        </Card>
        {active && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">{active.name}</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => duplicate(active)}><Copy className="mr-2 h-4 w-4" />Duplicate</Button>
                <Button variant="outline" size="sm" onClick={() => setPreview(preview === "html" ? null : "html")}><Eye className="mr-2 h-4 w-4" />Preview</Button>
                <Button variant="outline" size="sm" onClick={() => setPreview(preview === "pdf" ? null : "pdf")}><FileText className="mr-2 h-4 w-4" />PDF preview</Button>
                <Button variant="outline" size="sm" onClick={() => remove.mutate(active)} disabled={!active.id || remove.isPending}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Template key"><Input value={active.template_key} onChange={(event) => patch({ template_key: event.target.value })} disabled={Boolean(active.id)} /></Field>
                <Field label="Name"><Input value={active.name} onChange={(event) => patch({ name: event.target.value })} /></Field>
                <Field label="Language"><Select value={active.language} onValueChange={(value) => patch({ language: value as "fr" | "en" })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="fr">FR</SelectItem><SelectItem value="en">EN</SelectItem></SelectContent></Select></Field>
                <Field label="Company name"><Input value={active.company_name} onChange={(event) => patch({ company_name: event.target.value })} /></Field>
                <Field label="Logo URL"><Input value={active.company_logo_url ?? ""} onChange={(event) => patch({ company_logo_url: event.target.value })} /></Field>
                <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Active</Label><Switch checked={active.is_enabled} onCheckedChange={(checked) => patch({ is_enabled: checked })} /></div>
              </div>
              <Field label="Company legal fields"><Textarea value={active.company_legal_details ?? ""} onChange={(event) => patch({ company_legal_details: event.target.value })} /></Field>
              <Field label="Payment terms"><Textarea value={active.payment_terms ?? ""} onChange={(event) => patch({ payment_terms: event.target.value })} /></Field>
              <Field label="Footer/legal text"><Textarea value={active.footer_text ?? ""} onChange={(event) => patch({ footer_text: event.target.value })} /></Field>
              {preview ? (
                <div className={preview === "pdf" ? "mx-auto max-w-[794px] rounded-md border border-border bg-white p-10 text-black shadow-sm" : "rounded-md border border-border p-4"} dangerouslySetInnerHTML={{ __html: safeTemplateHTML(renderedHtml) }} />
              ) : (
                <Field label="HTML/PDF layout"><Textarea className="min-h-60 font-mono text-xs" value={active.html_body} onChange={(event) => patch({ html_body: event.target.value })} /></Field>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}
